import { api } from '@/services/api';
import type {
  AdditionalFieldMeta,
  AssortmentSearchResult,
  BulkUpdateCatalogAssortmentsRequest,
  CartItem,
  CommitTransferError,
  CommitTransferResult,
  CreateInventoryAssortmentRequest,
  CreateInventorySectionRequest,
  CreateReceiptRequest,
  EquipmentDetailsResponse,
  EquipmentSearchResult,
  GroupDecommissionResponse,
  GroupPrintMode,
  GroupPrintResponse,
  InventoryAttachment,
  InventoryCatalogAssortmentDetails,
  InventoryCatalogAssortmentListResponse,
  InventoryCatalogAssortmentLiveStatus,
  InventoryCatalogLiveSnapshot,
  InventoryCatalogSectionLiveStatus,
  InventoryDetails,
  InventoryDocumentTemplate,
  InventoryEquipmentSnapshot,
  InventoryFilter,
  InventoryHistoryParams,
  InventoryHistoryResponse,
  InventoryListItem,
  InventoryTransferSearchItem,
  InventoryListResponse,
  InventoryOperationDetails,
  InventoryPrintJob,
  InventoryPrintJobStatus,
  InventoryRelations,
  InventorySectionNode,
  MergeCatalogAssortmentsRequest,
  MoveCatalogAssortmentsRequest,
  OperationAddItemsPayload,
  OperationDocumentResponse,
  OperationRemoveItemPayload,
  OperationUpdatePayload,
  ReorderCatalogSectionsRequest,
  ReservReportItem,
  SaveWarehouseSettingsRequest,
  SplitRequest,
  SyncCartError,
  SyncCartResult,
  TraderShort,
  TraderMutationRequest,
  TransferDraft,
  TransferReportItem,
  TransferRequest,
  UpdateInventoryAssortmentRequest,
  UpdateInventoryPayload,
  UpdateInventorySectionRequest,
  WarehouseAccessCandidate,
  WarehouseSettingsDetail,
  WarehouseSettingsListItem,
  WarehouseShort,
} from '../types';

type AttachmentScope = 'inventory' | 'assortment';

const attachmentScopeBasePath: Record<AttachmentScope, string> = {
  inventory: '/inventory/items',
  assortment: '/inventory/catalog/assortments',
};

const buildQueryParams = (filter: InventoryFilter): string => {
  const params = new URLSearchParams();

  if (filter.query) params.set('query', filter.query);
  if (filter.page) params.set('page', filter.page.toString());
  if (filter.limit) params.set('limit', filter.limit.toString());
  if (filter.sortBy) params.set('sortBy', filter.sortBy);
  if (filter.sortDesc !== undefined) params.set('sortDesc', String(filter.sortDesc));

  if (filter.accountType !== undefined) params.set('accountType', filter.accountType.toString());
  if (filter.accountSub !== undefined) params.set('accountSub', filter.accountSub.toString());
  if (filter.accountObjectId !== undefined) params.set('accountObjectId', filter.accountObjectId.toString());

  if (filter.inventorySectionIds?.length) {
    params.set('sectionIds', filter.inventorySectionIds.join(','));
  }

  if (filter.inventoryAssortmentIds?.length) {
    params.set('assortmentIds', filter.inventoryAssortmentIds.join(','));
  }

  if (filter.locationTypes?.length) {
    params.set('locationTypes', filter.locationTypes.join(','));
  }

  if (filter.locationObjectIds?.length) {
    params.set('locationObjectIds', filter.locationObjectIds.join(','));
  }

  if (filter.documentDate) params.set('documentDate', filter.documentDate);
  if (filter.documentNumber) params.set('documentNumber', filter.documentNumber);
  if (filter.inventoryNumber) params.set('inventoryNumber', filter.inventoryNumber);
  if (filter.serialNumber) params.set('serialNumber', filter.serialNumber);
  if (filter.macAddress) params.set('macAddress', filter.macAddress);
  if (filter.ipAddress) params.set('ipAddress', filter.ipAddress);
  if (filter.warningsOnly) params.set('warningsOnly', 'true');

  return params.toString();
};

const buildHistorySearchParams = (params?: InventoryHistoryParams): URLSearchParams => {
  const searchParams = new URLSearchParams();
  if (!params) {
    return searchParams;
  }

  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.cursor) searchParams.set('cursor', params.cursor);
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  if (params.sort) searchParams.set('sort', params.sort);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.direction && params.direction !== 'any') {
    searchParams.set('direction', params.direction);
  }
  params.types?.forEach((type) => {
    searchParams.append('type', type);
  });
  if (typeof params.accountType === 'number') searchParams.set('accountType', params.accountType.toString());
  if (typeof params.accountObjectId === 'number') searchParams.set('accountObjectId', params.accountObjectId.toString());
  if (typeof params.warehouseId === 'number') searchParams.set('warehouseId', params.warehouseId.toString());
  if (params.query) {
    const trimmed = params.query.trim();
    if (trimmed) {
      searchParams.set('q', trimmed);
    }
  }

  return searchParams;
};

const fetchInventoryList = async (filter: InventoryFilter): Promise<InventoryListResponse> => {
    const query = buildQueryParams(filter);
    const response = await api.get<InventoryListResponse>(`/inventory/items${query ? `?${query}` : ''}`);
    return response.data;
};

export const inventoryApi = {
  list: fetchInventoryList,

  searchItems: async (query: string, limit = 10): Promise<InventoryListItem[]> => {
    const trimmed = query.trim();
    if (!trimmed) {
      return [];
    }
    const response = await fetchInventoryList({
      query: trimmed,
      limit,
      page: 1,
    });
    return response.items ?? [];
  },

  details: async (id: number): Promise<InventoryDetails> => {
    const response = await api.get<InventoryDetails>(`/inventory/items/${id}`);
    return response.data;
  },

  history: async (id: number, params?: InventoryHistoryParams): Promise<InventoryHistoryResponse> => {
    const searchParams = buildHistorySearchParams(params);
    const query = searchParams.toString();
    const response = await api.get<InventoryHistoryResponse>(`/inventory/items/${id}/history${query ? `?${query}` : ''}`);
    return response.data;
  },

  exportHistory: async (
    id: number,
    params: InventoryHistoryParams | undefined,
    format: 'csv' | 'html',
  ): Promise<{ blob: Blob; filename: string }> => {
    const searchParams = buildHistorySearchParams(params);
    searchParams.set('format', format);
    const query = searchParams.toString();
    const response = await api.get<Blob>(`/inventory/items/${id}/history/export${query ? `?${query}` : ''}`, {
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] ?? '';
    const match = disposition.match(/filename=([^;]+)/i);
    const filename =
      match?.[1]?.replace(/"/g, '').trim() ?? `inventory-history-${id}-${new Date().toISOString().slice(0, 19)}.${format}`;
    return { blob: response.data, filename };
  },

  getRelations: async (id: number): Promise<InventoryRelations> => {
    const response = await api.get<InventoryRelations>(`/inventory/items/${id}/relations`);
    return response.data;
  },

  getEquipmentSnapshot: async (id: number): Promise<InventoryEquipmentSnapshot> => {
    const response = await api.get<InventoryEquipmentSnapshot>(`/inventory/items/${id}/equipment`);
    return response.data;
  },

  getEquipmentBatch: async (ids: number[]): Promise<Record<number, InventoryEquipmentSnapshot | undefined>> => {
    if (!ids.length) {
      return {};
    }
    try {
      const response = await api.post<{ items: Array<{ id: number; snapshot?: InventoryEquipmentSnapshot }> }>(
        '/inventory/items/equipment/batch',
        { ids },
      );
      const map: Record<number, InventoryEquipmentSnapshot | undefined> = {};
      (response.data.items ?? []).forEach((row) => {
        map[row.id] = row.snapshot;
      });
      return map;
    } catch (error) {
      // fallback: по одному, чтобы не ломать UX, если backend-метод ещё не доступен
      const results: Record<number, InventoryEquipmentSnapshot | undefined> = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            results[id] = await inventoryApi.getEquipmentSnapshot(id);
          } catch {
            results[id] = undefined;
          }
        }),
      );
      return results;
    }
  },

  refreshEquipment: async (assortmentId: number): Promise<InventoryEquipmentSnapshot> => {
    const response = await api.post<InventoryEquipmentSnapshot>(
      `/inventory/catalog/assortments/${assortmentId}/equipment/refresh`,
    );
    return response.data;
  },

  refreshAssortmentEquipment: async (assortmentId: number): Promise<InventoryEquipmentSnapshot> => {
    const response = await api.post<InventoryEquipmentSnapshot>(
      `/inventory/catalog/assortments/${assortmentId}/equipment/refresh`,
    );
    return response.data;
  },

  catalogSectionIntegration: async (sectionId: number): Promise<InventoryCatalogSectionLiveStatus> => {
    const response = await api.get<InventoryCatalogSectionLiveStatus>(`/inventory/catalog/sections/${sectionId}/integration`);
    return response.data;
  },

  catalogAssortmentLive: async (assortmentId: number): Promise<InventoryCatalogAssortmentLiveStatus> => {
    const response = await api.get<InventoryCatalogAssortmentLiveStatus>(`/inventory/catalog/assortments/${assortmentId}/live`);
    return response.data;
  },

  catalogLiveSnapshot: async (): Promise<InventoryCatalogLiveSnapshot> => {
    const response = await api.get<InventoryCatalogLiveSnapshot>('/inventory/catalog/live/snapshot');
    return response.data;
  },

  updateItem: async (inventoryId: number, payload: UpdateInventoryPayload): Promise<InventoryDetails> => {
    const response = await api.patch<InventoryDetails>(`/inventory/items/${inventoryId}`, payload);
    return response.data;
  },

  createReceipt: async (payload: CreateReceiptRequest): Promise<InventoryDetails> => {
    const response = await api.post<InventoryDetails>('/inventory/receipts', payload);
    return response.data;
  },

  transfer: async (payload: TransferRequest): Promise<InventoryDetails> => {
    const response = await api.post<InventoryDetails>('/inventory/transfers', payload);
    return response.data;
  },

  split: async (inventoryId: number, payload: SplitRequest): Promise<InventoryDetails> => {
    const response = await api.post<InventoryDetails>(`/inventory/items/${inventoryId}/split`, payload);
    return response.data;
  },

  listWarehouses: async (query?: string, limit?: number): Promise<WarehouseShort[]> => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (limit) params.set('limit', String(limit));
    const queryString = params.toString();
    const response = await api.get<{ items: WarehouseShort[] }>(
      `/inventory/references/warehouses${queryString ? `?${queryString}` : ''}`,
    );
    return response.data.items;
  },

  // inventory-transfer.1 — read-only warehouse search with honest availability.
  searchTransferCandidates: async (
    storageId: number,
    search: string,
  ): Promise<InventoryTransferSearchItem[]> => {
    const params = new URLSearchParams({ storage: String(storageId), search });
    const response = await api.get<{ result: string; data: InventoryTransferSearchItem[] }>(
      `/inventory/transfers/search?${params.toString()}`,
    );
    return response.data.data;
  },

  // inventory-transfer.2 — cart/draft sync, token+revision optimistic lock.
  // Conflict outcomes (reservation_stale/reservation_conflict/reserved_by_other)
  // come back at 200 (expected user-facing outcomes, not transport errors) —
  // callers check `result`, not a caught exception.
  syncTransferCart: async (payload: {
    storageId: number;
    recipientEmployeeId?: number | null;
    reservationToken: string;
    reservationRevision: number;
    items: CartItem[];
  }): Promise<{ result: 'OK' } & SyncCartResult | SyncCartError> => {
    const response = await api.post<{ result: 'OK' } & SyncCartResult | SyncCartError>(
      '/inventory/transfers/cart/sync',
      payload,
    );
    return response.data;
  },

  getActiveTransferDrafts: async (): Promise<TransferDraft[]> => {
    const response = await api.get<{ result: string; data: TransferDraft[] }>('/inventory/transfers/cart/drafts');
    return response.data.data;
  },

  releaseTransferCart: async (reservationToken: string, reservationRevision: number): Promise<boolean> => {
    const response = await api.post<{ result: string; released: boolean }>('/inventory/transfers/cart/release', {
      reservationToken,
      reservationRevision,
    });
    return response.data.released;
  },

  // inventory-transfer.3 — commit the cart into an actual transfer. Same
  // 200-carries-expected-errors convention as syncTransferCart.
  commitTransferCart: async (payload: {
    storageId: number;
    recipientEmployeeId: number;
    reservationToken: string;
    reservationRevision: number;
    items: CartItem[];
    clientRequestId: string;
  }): Promise<CommitTransferResult | CommitTransferError> => {
    const response = await api.post<CommitTransferResult | CommitTransferError>(
      '/inventory/transfers/cart/commit',
      payload,
    );
    return response.data;
  },

  listTraders: async (query?: string, limit?: number): Promise<TraderShort[]> => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (limit) params.set('limit', String(limit));
    const queryString = params.toString();
    const response = await api.get<{ items: TraderShort[] }>(
      `/inventory/references/traders${queryString ? `?${queryString}` : ''}`,
    );
    return response.data.items;
  },

  createTrader: async (payload: TraderMutationRequest): Promise<TraderShort> => {
    const response = await api.post<TraderShort>('/inventory/references/traders', payload);
    return response.data;
  },

  updateTrader: async (traderId: number, payload: TraderMutationRequest): Promise<TraderShort> => {
    const response = await api.put<TraderShort>(`/inventory/references/traders/${traderId}`, payload);
    return response.data;
  },

  deleteTrader: async (traderId: number): Promise<void> => {
    await api.delete(`/inventory/references/traders/${traderId}`);
  },

  listWarehouseSettings: async (): Promise<WarehouseSettingsListItem[]> => {
    const response = await api.get<{ items: WarehouseSettingsListItem[] }>('/inventory/warehouses');
    return response.data.items;
  },

  getWarehouseSettings: async (id: number): Promise<WarehouseSettingsDetail> => {
    const response = await api.get<WarehouseSettingsDetail>(`/inventory/warehouses/${id}`);
    return response.data;
  },

  listWarehouseAccessCandidates: async (): Promise<WarehouseAccessCandidate[]> => {
    const response = await api.get<{ items: WarehouseAccessCandidate[] }>('/inventory/warehouses/access-candidates');
    return response.data.items;
  },

  createWarehouseSettings: async (payload: SaveWarehouseSettingsRequest): Promise<{ id: number }> => {
    const response = await api.post<{ id: number }>('/inventory/warehouses', payload);
    return response.data;
  },

  updateWarehouseSettings: async (id: number, payload: SaveWarehouseSettingsRequest): Promise<{ id: number }> => {
    const response = await api.put<{ id: number }>(`/inventory/warehouses/${id}`, payload);
    return response.data;
  },

  deleteWarehouseSettings: async (id: number): Promise<void> => {
    await api.delete(`/inventory/warehouses/${id}`);
  },

  searchAssortments: async (query?: string, sectionId?: number, limit?: number): Promise<AssortmentSearchResult[]> => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (sectionId) params.set('sectionId', sectionId.toString());
    if (limit) params.set('limit', String(limit));
    const queryString = params.toString();
    const response = await api.get<{ items: AssortmentSearchResult[] }>(
      `/inventory/references/assortments${queryString ? `?${queryString}` : ''}`,
    );
    return response.data.items;
  },

  getReservReport: async (warehouseIds?: number[]): Promise<ReservReportItem[]> => {
    const params = new URLSearchParams();
    if (warehouseIds?.length) {
      params.set('warehouseIds', warehouseIds.join(','));
    }
    const queryString = params.toString();
    const response = await api.get<{ items: ReservReportItem[] }>(
      `/inventory/reports/reserv${queryString ? `?${queryString}` : ''}`,
    );
    return response.data.items;
  },

  getTransferReport: async (args: { dateFrom: string; dateTo: string; sourceAccountType?: number; destinationAccountType?: number; includePurchase?: boolean }): Promise<TransferReportItem[]> => {
    const params = new URLSearchParams();
    params.set('dateFrom', args.dateFrom);
    params.set('dateTo', args.dateTo);
    if (args.sourceAccountType !== undefined) params.set('sourceAccountType', String(args.sourceAccountType));
    if (args.destinationAccountType !== undefined) params.set('destinationAccountType', String(args.destinationAccountType));
    if (args.includePurchase) params.set('includePurchase', '1');
    const queryString = params.toString();
    const response = await api.get<{ items: TransferReportItem[] }>(
      `/inventory/reports/transfer${queryString ? `?${queryString}` : ''}`,
    );
    return response.data.items;
  },

  renderOperationDocument: async (
    operationId: number,
    options?: { templateId?: number },
  ): Promise<OperationDocumentResponse> => {
    const params = new URLSearchParams();
    if (options?.templateId) {
      params.set('templateId', options.templateId.toString());
    }
    const query = params.toString();
    const response = await api.get<OperationDocumentResponse>(
      `/inventory/operations/${operationId}/document${query ? `?${query}` : ''}`,
    );
    return response.data;
  },

  transitApprove: async (payload: {
    ids: number[];
    tokens: Record<number, number>;
    comment?: string;
  }): Promise<void> => {
    await api.post('/inventory/group/transit/approve', payload);
  },

  transitCancel: async (payload: {
    ids: number[];
    tokens: Record<number, number>;
    comment?: string;
  }): Promise<void> => {
    await api.post('/inventory/group/transit/cancel', payload);
  },

  decommission: async (payload: {
    ids: number[];
    tokens: Record<number, number>;
  }): Promise<GroupDecommissionResponse> => {
    const response = await api.post<GroupDecommissionResponse>('/inventory/group/decommission', payload);
    return response.data;
  },

  preparePrint: async (ids: number[], mode: GroupPrintMode): Promise<GroupPrintResponse> => {
    const response = await api.post<GroupPrintResponse>('/inventory/group/print', { ids, mode });
    return response.data;
  },

  exportPrint: async (
    ids: number[],
    mode: GroupPrintMode,
    format: 'html' | 'pdf',
    options?: { templateId?: number },
  ): Promise<{ blob: Blob; filename: string }> => {
    const payload: Record<string, unknown> = { ids, mode, format };
    if (options?.templateId) {
      payload.templateId = options.templateId;
    }

    const response = await api.post<Blob>('/inventory/group/print/export', payload, {
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] ?? '';
    const match = disposition.match(/filename=([^;]+)/i);
    const filename = match?.[1]?.replace(/"/g, '').trim() ?? `inventory-print-${mode}.${format}`;
    return { blob: response.data, filename };
  },

  enqueuePrintJob: async (payload: {
    ids: number[];
    mode?: GroupPrintMode;
    format?: 'html' | 'pdf';
    templateId?: number | null;
  }): Promise<InventoryPrintJob> => {
    const body: Record<string, unknown> = {
      ids: payload.ids,
      mode: payload.mode ?? 'barcode',
      format: payload.format ?? 'pdf',
    };
    if (payload.templateId) {
      body.templateId = payload.templateId;
    }
    const response = await api.post<InventoryPrintJob>('/inventory/group/print/queue', body);
    return response.data;
  },

  listPrintJobs: async (params?: {
    status?: InventoryPrintJobStatus[];
    limit?: number;
    offset?: number;
  }): Promise<{ items: InventoryPrintJob[]; total: number }> => {
    const searchParams = new URLSearchParams();
    params?.status?.forEach((status) => {
      searchParams.append('status', status);
    });
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    const response = await api.get<{ items: InventoryPrintJob[]; total: number }>(
      `/inventory/print-jobs${query ? `?${query}` : ''}`,
    );
    return response.data;
  },

  getPrintJob: async (jobId: string): Promise<InventoryPrintJob> => {
    const response = await api.get<InventoryPrintJob>(`/inventory/print-jobs/${jobId}`);
    return response.data;
  },

  downloadPrintJob: async (jobId: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get<Blob>(`/inventory/print-jobs/${jobId}/download`, {
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] ?? '';
    const match = disposition.match(/filename=([^;]+)/i);
    const filename = match?.[1]?.replace(/"/g, '').trim() ?? `inventory-print-job-${jobId}.pdf`;
    return { blob: response.data, filename };
  },

  listDocumentTemplates: async (): Promise<InventoryDocumentTemplate[]> => {
    const response = await api.get<{ items: InventoryDocumentTemplate[] }>('/inventory/document-templates');
    return response.data.items;
  },

  catalogSections: async (opts?: { includeResponsible?: boolean }): Promise<InventorySectionNode[]> => {
    const params = new URLSearchParams();
    if (opts?.includeResponsible) {
      params.set('includeResponsible', 'true');
    }
    const query = params.toString();
    const response = await api.get<{ items: InventorySectionNode[] }>(
      `/inventory/catalog/sections${query ? `?${query}` : ''}`,
    );
    return response.data.items;
  },

  createCatalogSection: async (payload: CreateInventorySectionRequest): Promise<InventorySectionNode> => {
    const response = await api.post<InventorySectionNode>('/inventory/catalog/sections', normalizeSectionPayload(payload));
    return response.data;
  },

  updateCatalogSection: async (
    sectionId: number,
    payload: UpdateInventorySectionRequest,
  ): Promise<InventorySectionNode> => {
    const response = await api.put<InventorySectionNode>(
      `/inventory/catalog/sections/${sectionId}`,
      normalizeSectionPayload(payload),
    );
    return response.data;
  },

  deleteCatalogSection: async (sectionId: number): Promise<void> => {
    await api.delete(`/inventory/catalog/sections/${sectionId}`);
  },

  reorderCatalogSections: async (payload: ReorderCatalogSectionsRequest): Promise<void> => {
    await api.put('/inventory/catalog/sections/reorder', payload);
  },

  catalogAssortments: async (
    sectionId: number,
    params: {
      page?: number;
      limit?: number;
      query?: string;
      typeId?: number;
      sortBy?: string;
      sortDesc?: boolean;
    },
  ): Promise<InventoryCatalogAssortmentListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.query) searchParams.set('query', params.query);
    if (params.typeId !== undefined) searchParams.set('typeId', params.typeId.toString());
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortDesc !== undefined) searchParams.set('sortDesc', String(params.sortDesc));
    const query = searchParams.toString();
    const response = await api.get<InventoryCatalogAssortmentListResponse>(
      `/inventory/catalog/sections/${sectionId}/assortments${query ? `?${query}` : ''}`,
    );
    return response.data;
  },

  createCatalogAssortment: async (
    payload: CreateInventoryAssortmentRequest,
  ): Promise<InventoryCatalogAssortmentDetails> => {
    const response = await api.post<InventoryCatalogAssortmentDetails>(
      '/inventory/catalog/assortments',
      normalizeAssortmentPayload(payload),
    );
    return response.data;
  },

  updateCatalogAssortment: async (
    assortmentId: number,
    payload: UpdateInventoryAssortmentRequest,
  ): Promise<InventoryCatalogAssortmentDetails> => {
    const response = await api.put<InventoryCatalogAssortmentDetails>(
      `/inventory/catalog/assortments/${assortmentId}`,
      normalizeAssortmentPayload(payload),
    );
    return response.data;
  },

  deleteCatalogAssortment: async (assortmentId: number): Promise<void> => {
    await api.delete(`/inventory/catalog/assortments/${assortmentId}`);
  },

  moveCatalogAssortments: async (payload: MoveCatalogAssortmentsRequest): Promise<void> => {
    await api.post('/inventory/catalog/assortments/move', payload);
  },

  mergeCatalogAssortments: async (
    payload: MergeCatalogAssortmentsRequest,
  ): Promise<InventoryCatalogAssortmentDetails> => {
    const response = await api.post<InventoryCatalogAssortmentDetails>(
      '/inventory/catalog/assortments/merge',
      payload,
    );
    return response.data;
  },

  bulkUpdateCatalogAssortments: async (
    payload: BulkUpdateCatalogAssortmentsRequest,
  ): Promise<void> => {
    await api.post('/inventory/catalog/assortments/bulk/update', payload);
  },

  catalogAssortmentDetails: async (assortmentId: number): Promise<InventoryCatalogAssortmentDetails> => {
    const response = await api.get<InventoryCatalogAssortmentDetails>(`/inventory/catalog/assortments/${assortmentId}`);
    return response.data;
  },

  catalogAdditionalFields: async (): Promise<AdditionalFieldMeta[]> => {
    const response = await api.get<{ items: AdditionalFieldMeta[] }>('/inventory/catalog/additional-fields');
    return response.data.items;
  },

  catalogEquipmentSearch: async (query: string): Promise<EquipmentSearchResult> => {
    const trimmed = query.trim();
    if (!trimmed) {
      return { items: [], licenseInactive: false };
    }
    const response = await api.get<EquipmentSearchResult>(
      `/inventory/catalog/equipment/search?q=${encodeURIComponent(trimmed)}`,
    );
    return response.data;
  },

  catalogEquipmentDetails: async (modelId: string): Promise<EquipmentDetailsResponse> => {
    const response = await api.get<EquipmentDetailsResponse>(
      `/inventory/catalog/equipment/${encodeURIComponent(modelId)}`,
    );
    return response.data;
  },

  getOperationDetails: async (operationId: number): Promise<InventoryOperationDetails> => {
    const response = await api.get<InventoryOperationDetails>(`/inventory/operations/${operationId}`);
    return response.data;
  },

  addOperationItems: async (operationId: number, payload: OperationAddItemsPayload): Promise<InventoryOperationDetails> => {
    const response = await api.post<InventoryOperationDetails>(`/inventory/operations/${operationId}/items`, payload);
    return response.data;
  },

  removeOperationItem: async (
    operationId: number,
    inventoryId: number,
    payload: OperationRemoveItemPayload,
  ): Promise<InventoryOperationDetails> => {
    const response = await api.delete<InventoryOperationDetails>(
      `/inventory/operations/${operationId}/items/${inventoryId}`,
      {
        data: payload,
      },
    );
    return response.data;
  },

  updateOperation: async (
    operationId: number,
    payload: OperationUpdatePayload,
  ): Promise<InventoryOperationDetails> => {
    const response = await api.patch<InventoryOperationDetails>(`/inventory/operations/${operationId}`, payload);
    return response.data;
  },

  listAttachments: async (scope: AttachmentScope, objectId: number): Promise<InventoryAttachment[]> => {
    const response = await api.get<{ items: InventoryAttachment[] }>(
      `${attachmentScopeBasePath[scope]}/${objectId}/attachments`,
    );
    return response.data.items;
  },

  uploadAttachment: async (
    scope: AttachmentScope,
    objectId: number,
    payload: { file: File | Blob; comment?: string },
  ): Promise<InventoryAttachment> => {
    const formData = new FormData();
    const filename = payload.file instanceof File ? payload.file.name : 'attachment';
    formData.append('file', payload.file, filename);
    if (payload.comment) {
      formData.append('comment', payload.comment);
    }

    const response = await api.post<{ item: InventoryAttachment }>(
      `${attachmentScopeBasePath[scope]}/${objectId}/attachments`,
      formData,
    );
    return response.data.item;
  },

  deleteAttachment: async (scope: AttachmentScope, objectId: number, uuid: string): Promise<InventoryAttachment> => {
    const response = await api.delete<{ item: InventoryAttachment }>(
      `${attachmentScopeBasePath[scope]}/${objectId}/attachments/${uuid}`,
    );
    return response.data.item;
  },
};

export const inventoryKeys = {
  all: ['inventory'] as const,
  list: (filter: InventoryFilter) => [...inventoryKeys.all, 'list', filter] as const,
  details: (id: number) => [...inventoryKeys.all, 'details', id] as const,
  attachments: (scope: AttachmentScope, id: number) => [...inventoryKeys.all, 'attachments', scope, id] as const,
  history: (id: number, params?: InventoryHistoryParams) => [
    ...inventoryKeys.all,
    'history',
    id,
    params ?? {},
  ] as const,
  relations: (id: number) => [...inventoryKeys.all, 'relations', id] as const,
  equipment: (id: number) => [...inventoryKeys.all, 'equipment', id] as const,
  equipmentBatch: (ids: number[]) => [...inventoryKeys.all, 'equipment', 'batch', ...(ids ?? [])] as const,
  operation: (id: number) => [...inventoryKeys.all, 'operation', id] as const,
  warehouses: (query?: string) => [...inventoryKeys.all, 'warehouses', query ?? ''] as const,
  warehouseSettings: () => [...inventoryKeys.all, 'warehouse-settings'] as const,
  warehouseSettingsDetail: (id: number) => [...inventoryKeys.all, 'warehouse-settings', id] as const,
  warehouseAccessCandidates: () => [...inventoryKeys.all, 'warehouse-settings', 'access-candidates'] as const,
  traders: (query?: string) => [...inventoryKeys.all, 'traders', query ?? ''] as const,
  assortments: (query?: string, sectionId?: number) => [...inventoryKeys.all, 'assortments', query ?? '', sectionId ?? ''] as const,
  reservReport: (warehouseIds?: number[]) => [...inventoryKeys.all, 'reports', 'reserv', warehouseIds ?? []] as const,
  transferReport: (params: { dateFrom: string; dateTo: string; source?: number; destination?: number; includePurchase?: boolean }) => [
    ...inventoryKeys.all,
    'reports',
    'transfer',
    params.dateFrom,
    params.dateTo,
    params.source ?? 'any',
    params.destination ?? 'any',
    params.includePurchase ? 'purchase' : 'nopurchase',
  ] as const,
  operationDocument: (operationId: number) => [...inventoryKeys.all, 'operation-document', operationId] as const,
  groupPrint: (ids: number[], mode: GroupPrintMode) => [...inventoryKeys.all, 'group-print', mode, ...ids] as const,
  documentTemplates: () => [...inventoryKeys.all, 'document-templates'] as const,
  printJobs: (params?: { status?: InventoryPrintJobStatus[]; limit?: number; offset?: number }) =>
    [...inventoryKeys.all, 'print-jobs', params?.status ?? [], params?.limit ?? 'auto', params?.offset ?? 'auto'] as const,
  printJob: (jobId: string) => [...inventoryKeys.all, 'print-job', jobId] as const,
  catalogSections: () => [...inventoryKeys.all, 'catalog', 'sections'] as const,
  catalogAssortments: (sectionId: number, params: { page?: number; limit?: number; query?: string; typeId?: number; sortBy?: string; sortDesc?: boolean }) =>
    [...inventoryKeys.all, 'catalog', 'assortments', sectionId, params] as const,
  catalogAssortmentDetails: (assortmentId: number) => [...inventoryKeys.all, 'catalog', 'assortment', assortmentId] as const,
  catalogAssortmentLive: (assortmentId: number) => [...inventoryKeys.all, 'catalog', 'assortment', 'live', assortmentId] as const,
  catalogAssortmentLiveIdle: () => [...inventoryKeys.all, 'catalog', 'assortment', 'live', 'idle'] as const,
  catalogAdditionalFields: () => [...inventoryKeys.all, 'catalog', 'additional-fields'] as const,
  catalogEquipmentSearch: (query: string) => [...inventoryKeys.all, 'catalog', 'equipment', 'search', query] as const,
  catalogEquipmentDetails: (id: string) => [...inventoryKeys.all, 'catalog', 'equipment', 'details', id] as const,
  catalogIntegration: (sectionId: number) => [...inventoryKeys.all, 'catalog', 'integration', sectionId] as const,
  catalogIntegrationIdle: () => [...inventoryKeys.all, 'catalog', 'integration', 'idle'] as const,
  catalogLiveSnapshot: () => [...inventoryKeys.all, 'catalog', 'live', 'snapshot'] as const,
};

function normalizeSectionPayload<
  T extends CreateInventorySectionRequest | UpdateInventorySectionRequest,
>(payload: T): T {
  const result: Record<string, unknown> = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    if (key === 'additionalFieldIds' || key === 'responsibleEmployeeIds') {
      result[key] = (value as number[]).filter((id) => Number.isFinite(id));
      return;
    }

    result[key] = value;
  });

  return result as T;
}

function normalizeAssortmentPayload<
  T extends CreateInventoryAssortmentRequest | UpdateInventoryAssortmentRequest,
>(payload: T): T {
  const result: Record<string, unknown> = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    if (key === 'additionalFieldIds') {
      result[key] = (value as number[]).filter((id) => Number.isFinite(id));
      return;
    }

    if (key === 'warehouseReserves' && value) {
      const reserves = value as Record<number, number>;
      result[key] = Object.fromEntries(
        Object.entries(reserves)
          .filter(([, amount]) => Number.isFinite(amount))
          .map(([warehouseId, amount]) => [warehouseId.toString(), amount]),
      );
      return;
    }

    result[key] = value;
  });

  return result as T;
}
